import { useTaskStore } from "@/stores/taskStore";

/** The text of the task's last user message, or null when it typed none. */
function lastUserText(taskId?: string | null): string | null {
  const store = useTaskStore.getState();
  const id = taskId ?? store.selectedTaskId;
  const task = id ? store.tasks.get(id) : null;
  if (!task) return null;
  const lastUserMsg = [...task.messages]
    .reverse()
    .find((m) => m.role === "user");
  const lastText = lastUserMsg?.content?.find(
    (b: { type: string }) => b.type === "text"
  );
  if (
    !lastText ||
    !("text" in lastText) ||
    typeof lastText.text !== "string" ||
    lastText.text === ""
  ) {
    return null;
  }
  return lastText.text;
}

/**
 * The ONE retry path a failed turn offers.
 *
 * Re-sends the task's last user message, which is what "Try again" has always
 * meant on the ErrorBlock card — extracted here so the turn footer's Retry
 * affordance (added when a failed turn reloaded as a serene completion, staging
 * task 47faf57f) drives exactly the same resend instead of growing a second,
 * drifting implementation.
 *
 * Never throws. Returns TRUE if a message was resent; FALSE when there is no
 * user text to resend, in which case the caller's fallback (focus the composer)
 * is the honest outcome.
 *
 * @param taskId The task the affordance belongs to. Omitted → the selected task.
 */
export function resendLastUserMessage(taskId?: string | null): boolean {
  const text = lastUserText(taskId);
  if (text === null) return false;
  useTaskStore.getState().sendMessage(text);
  return true;
}

/**
 * Start the task's last ask over as a NEW task on `model`. The way out of a
 * failure that is the model's own (its service, its key, its account): a
 * task's model is fixed when it is born, so no retry inside it gets past one.
 *
 * Returns the new task's id, for the caller to open; null when there is no
 * typed ask to carry over.
 */
export function startAgainOn(taskId: string, model: string): string | null {
  const text = lastUserText(taskId);
  if (text === null) return null;
  const store = useTaskStore.getState();
  store.createNewTask();
  store.sendMessage(text, undefined, undefined, { model });
  return useTaskStore.getState().selectedTaskId;
}

/** Fallback when there is nothing to resend: put the cursor where the user can retype. */
export function focusComposer(): void {
  document.querySelector<HTMLTextAreaElement>("textarea")?.focus();
}
